import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import useMediaQuery from "../../../../hooks/useMediaQuery/useMediaQuery";
import usePersistedState from "../../../../hooks/usePersistedState/usePersistedState";
import { groupeDeLaRoute } from "./visibiliteMenu";

export const CLE_EPINGLE = "perceval-bo.menu.epingle";
// Tiroir sous 992 px de large, et aussi sous 570 px de haut : le rail ne
// tient plus dans la hauteur sans défiler (voir menu-lateral.scss).
export const REQUETE_MOBILE = "(max-width: 991px), (max-height: 569px)";
export const DELAI_OUVERTURE_SURVOL = 120;
export const DELAI_FERMETURE_SURVOL = 200;

// État du menu latéral.
// - mode : "rail" (64 px, défaut), "epingle" (264 px, mémorisé par navigateur)
//   ou "tiroir" (< 992 px de large ou < 570 px de haut : menu hors écran,
//   ouvert par la barre haute).
// - un seul groupe ouvert à la fois : volet par-dessus le contenu en rail
//   (clic, ou survol après 120 ms, refermé 200 ms après la sortie), accordéon
//   en épinglé et en tiroir, où le groupe de la page courante s'ouvre seul.
// - tout se referme sur Échap, clic hors du menu et changement de route.
export default function useMenuLateral(items) {
  const { pathname } = useLocation();
  const mobile = useMediaQuery(REQUETE_MOBILE);
  const [epingle, setEpingle] = usePersistedState(CLE_EPINGLE, false);
  const [tiroirOuvert, setTiroirOuvert] = useState(false);
  const [compteOuvert, setCompteOuvert] = useState(false);
  // { nom, parSurvol } : un groupe ouvert au survol se referme quand la souris
  // le quitte ; un groupe ouvert au clic reste ouvert.
  const [groupe, setGroupe] = useState(null);
  const refNav = useRef(null);
  const minuteries = useRef({ ouverture: null, fermeture: null });

  const mode = mobile ? "tiroir" : epingle ? "epingle" : "rail";
  const accordeon = mode !== "rail";
  const nomGroupeRoute = groupeDeLaRoute(items, pathname);

  const annulerMinuteries = useCallback(() => {
    clearTimeout(minuteries.current.ouverture);
    clearTimeout(minuteries.current.fermeture);
  }, []);

  useEffect(() => annulerMinuteries, [annulerMinuteries]);

  // Changement de route ou de mode : tout se referme ; en accordéon, le groupe
  // de la page courante s'ouvre.
  useEffect(() => {
    annulerMinuteries();
    setCompteOuvert(false);
    setTiroirOuvert(false);
    setGroupe(accordeon && nomGroupeRoute ? { nom: nomGroupeRoute, parSurvol: false } : null);
  }, [pathname, accordeon, nomGroupeRoute, annulerMinuteries]);

  // Échap et clic hors du menu.
  useEffect(() => {
    const surClavier = (evenement) => {
      if (evenement.key !== "Escape") return;
      setCompteOuvert(false);
      if (mode === "rail") setGroupe(null);
      if (mode === "tiroir") setTiroirOuvert(false);
    };
    const surClic = (evenement) => {
      if (refNav.current && refNav.current.contains(evenement.target)) return;
      setCompteOuvert(false);
      if (mode === "rail") setGroupe(null);
    };
    document.addEventListener("keydown", surClavier);
    document.addEventListener("click", surClic);
    return () => {
      document.removeEventListener("keydown", surClavier);
      document.removeEventListener("click", surClic);
    };
  }, [mode]);

  const basculerEpingle = useCallback(() => setEpingle((valeur) => !valeur), [setEpingle]);

  const basculerGroupe = useCallback(
    (nom) => {
      annulerMinuteries();
      setCompteOuvert(false);
      setGroupe((precedent) =>
        precedent && precedent.nom === nom && !precedent.parSurvol ? null : { nom, parSurvol: false }
      );
    },
    [annulerMinuteries]
  );

  const survolerGroupe = useCallback(
    (nom) => {
      if (mode !== "rail") return;
      annulerMinuteries();
      minuteries.current.ouverture = setTimeout(() => {
        setGroupe((precedent) => (precedent && precedent.nom === nom ? precedent : { nom, parSurvol: true }));
      }, DELAI_OUVERTURE_SURVOL);
    },
    [mode, annulerMinuteries]
  );

  const quitterGroupe = useCallback(() => {
    if (mode !== "rail") return;
    annulerMinuteries();
    minuteries.current.fermeture = setTimeout(() => {
      setGroupe((precedent) => (precedent && precedent.parSurvol ? null : precedent));
    }, DELAI_FERMETURE_SURVOL);
  }, [mode, annulerMinuteries]);

  const fermerGroupes = useCallback(() => {
    annulerMinuteries();
    setGroupe(null);
  }, [annulerMinuteries]);

  const basculerCompte = useCallback(() => {
    annulerMinuteries();
    if (mode === "rail") setGroupe(null);
    setCompteOuvert((valeur) => !valeur);
  }, [mode, annulerMinuteries]);
  const fermerCompte = useCallback(() => setCompteOuvert(false), []);
  const ouvrirTiroir = useCallback(() => setTiroirOuvert(true), []);
  const fermerTiroir = useCallback(() => setTiroirOuvert(false), []);

  return {
    mode,
    mobile,
    epingle,
    tiroirOuvert,
    compteOuvert,
    groupeOuvert: groupe ? groupe.nom : null,
    refNav,
    basculerEpingle,
    basculerGroupe,
    survolerGroupe,
    quitterGroupe,
    fermerGroupes,
    basculerCompte,
    fermerCompte,
    ouvrirTiroir,
    fermerTiroir,
  };
}
